import React, { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useAgencyClients } from "@/hooks/useAgencyClients";
import { PaywallModal } from "./paywall-modal";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import {
  Rocket,
  Plus,
  Minus,
  X,
  Bookmark,
  Loader2,
  CheckCircle2,
  AlertCircle,
  ShoppingBag,
  Layers,
  ArrowUpDown,
  Search,
  ChevronDown,
  ChevronUp,
  Package,
  Tag,
  DollarSign,
  Target,
  Eye,
  Settings2,
  Users,
} from "lucide-react";

// ============================================================
// Types
// ============================================================

type CampaignType = "zero-plus-minus" | "price-range" | "combined";
type AdPlacement = "search" | "product_page" | "left_column" | "display";

interface AdGroup {
  id: string;
  name: string;
  tier: "0" | "+1" | "-1";
  cpc: string;
  dailyBudget: string;
  offers: string[];
}

interface PriceRange {
  id: string;
  label: string;
  min: string;
  max: string;
}

interface AllegroCampaignConfig {
  campaignName: string;
  campaignType: CampaignType;
  placements: AdPlacement[];
  defaultCpc: string;
  defaultBudget: string;
  adGroups: AdGroup[];
  priceRanges: PriceRange[];
  useSubgroups: boolean; // combined: price range + 0/+1/-1 within each
}

interface LaunchResult {
  success: boolean;
  campaigns: {
    campaignId: string;
    campaignName: string;
    groups: { groupId: string; groupName: string; offersAdded: number }[];
  }[];
  errors: string[];
}

interface AllegroAdsPreset {
  id: string;
  label: string;
  description: string;
  config: Partial<AllegroCampaignConfig>;
}

// ============================================================
// Presets (static config — labels translated via buildAllegroPresets)
// ============================================================

const PRESET_STATIC_CONFIGS: Omit<AllegroAdsPreset, "label" | "description">[] = [
  {
    id: "dropshipping",
    config: {
      campaignType: "zero-plus-minus",
      placements: ["search"],
      defaultCpc: "1.00",
      defaultBudget: "10.00",
      adGroups: [
        {
          id: "g0",
          name: "Grupa 0 – Startowa",
          tier: "0",
          cpc: "1.00",
          dailyBudget: "10.00",
          offers: [],
        },
        {
          id: "g1",
          name: "Grupa +1 – Rokujące",
          tier: "+1",
          cpc: "1.25",
          dailyBudget: "15.00",
          offers: [],
        },
        {
          id: "g-1",
          name: "Grupa -1 – Słabsze",
          tier: "-1",
          cpc: "0.80",
          dailyBudget: "7.00",
          offers: [],
        },
      ],
      useSubgroups: false,
    },
  },
  {
    id: "price-segmentation",
    config: {
      campaignType: "price-range",
      placements: ["search"],
      defaultCpc: "1.00",
      defaultBudget: "10.00",
      priceRanges: [
        { id: "pr1", label: "100–200 zł", min: "100", max: "200" },
        { id: "pr2", label: "200–400 zł", min: "200", max: "400" },
        { id: "pr3", label: "400+ zł", min: "400", max: "" },
      ],
      useSubgroups: false,
      adGroups: [],
    },
  },
  {
    id: "combined",
    config: {
      campaignType: "combined",
      placements: ["search"],
      defaultCpc: "1.00",
      defaultBudget: "10.00",
      priceRanges: [
        { id: "pr1", label: "100–200 zł", min: "100", max: "200" },
        { id: "pr2", label: "200–400 zł", min: "200", max: "400" },
        { id: "pr3", label: "400+ zł", min: "400", max: "" },
      ],
      useSubgroups: true,
      adGroups: [
        {
          id: "g0",
          name: "Grupa 0 – Startowa",
          tier: "0",
          cpc: "1.00",
          dailyBudget: "10.00",
          offers: [],
        },
        {
          id: "g1",
          name: "Grupa +1 – Rokujące",
          tier: "+1",
          cpc: "1.25",
          dailyBudget: "15.00",
          offers: [],
        },
        {
          id: "g-1",
          name: "Grupa -1 – Słabsze",
          tier: "-1",
          cpc: "0.80",
          dailyBudget: "7.00",
          offers: [],
        },
      ],
    },
  },
];

function buildDefaultAdGroups(t: TFunction): AdGroup[] {
  return [
    {
      id: "g0",
      name: t("defaultGroups.tier0"),
      tier: "0",
      cpc: "1.00",
      dailyBudget: "10.00",
      offers: [],
    },
    {
      id: "g1",
      name: t("defaultGroups.tierPlus1"),
      tier: "+1",
      cpc: "1.25",
      dailyBudget: "15.00",
      offers: [],
    },
    {
      id: "g-1",
      name: t("defaultGroups.tierMinus1"),
      tier: "-1",
      cpc: "0.80",
      dailyBudget: "7.00",
      offers: [],
    },
  ];
}

function buildAllegroPresets(t: TFunction): AllegroAdsPreset[] {
  const defaultAdGroups = buildDefaultAdGroups(t);
  return [
    {
      ...PRESET_STATIC_CONFIGS[0],
      label: t("presets.dropshipping.label"),
      description: t("presets.dropshipping.description"),
      config: { ...PRESET_STATIC_CONFIGS[0].config, adGroups: defaultAdGroups },
    },
    {
      ...PRESET_STATIC_CONFIGS[1],
      label: t("presets.priceSegmentation.label"),
      description: t("presets.priceSegmentation.description"),
    },
    {
      ...PRESET_STATIC_CONFIGS[2],
      label: t("presets.combined.label"),
      description: t("presets.combined.description"),
      config: { ...PRESET_STATIC_CONFIGS[2].config, adGroups: defaultAdGroups },
    },
  ];
}

// ============================================================
// Constants
// ============================================================

function buildPlacementOptions(
  t: TFunction
): { value: AdPlacement; label: string; recommended: boolean; description: string }[] {
  return [
    {
      value: "search",
      label: t("placements.search.label"),
      recommended: true,
      description: t("placements.search.description"),
    },
    {
      value: "product_page",
      label: t("placements.productPage.label"),
      recommended: false,
      description: t("placements.productPage.description"),
    },
    {
      value: "left_column",
      label: t("placements.leftColumn.label"),
      recommended: false,
      description: t("placements.leftColumn.description"),
    },
    {
      value: "display",
      label: t("placements.display.label"),
      recommended: false,
      description: t("placements.display.description"),
    },
  ];
}

const TIER_COLORS = {
  "0": {
    bg: "bg-blue-500/15",
    border: "border-blue-500/30",
    text: "text-blue-300",
    dot: "bg-blue-400",
  },
  "+1": {
    bg: "bg-green-500/15",
    border: "border-green-500/30",
    text: "text-green-300",
    dot: "bg-green-400",
  },
  "-1": {
    bg: "bg-orange-500/15",
    border: "border-orange-500/30",
    text: "text-orange-300",
    dot: "bg-orange-400",
  },
};

// UI classes matching campaign-builder
const INPUT_CLS =
  "h-9 text-sm bg-[#030407] border-white/[0.1] text-white placeholder:text-white/30";
const SELECT_TRIGGER_CLS = "h-9 text-sm bg-[#030407] border-white/[0.1] text-white";
const SELECT_CONTENT_CLS = "bg-[#0a0c10] border-white/[0.1]";
const SELECT_ITEM_CLS = "text-white hover:bg-white/[0.08] text-sm";
const SECTION_CLS = "bg-white/[0.04] border border-white/[0.08] rounded-xl p-5 space-y-4";
const LABEL_CLS = "text-xs text-white/60 mb-1";

// ============================================================
// Helpers
// ============================================================

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;
}

function formatCampaignSummary(
  config: AllegroCampaignConfig,
  t: TFunction,
  placementOptions: { value: AdPlacement; label: string }[]
): string {
  const st = (key: string) => t(`summaryText.${key}`) as string;
  const typeLabels: Record<CampaignType, string> = {
    "zero-plus-minus": st("typeLabelZPM"),
    "price-range": st("typeLabelPR"),
    combined: st("typeLabelCombined"),
  };
  let summary = `📋 ${st("header")}\n`;
  summary += `${"═".repeat(50)}\n\n`;
  summary += `${st("name")}: ${config.campaignName || st("noName")}\n`;
  summary += `${st("type")}: ${typeLabels[config.campaignType]}\n`;
  summary += `${st("placementsLabel")}: ${config.placements.map((p) => placementOptions.find((o) => o.value === p)?.label).join(", ")}\n`;
  summary += `${st("cpcLabel")}: ${config.defaultCpc} zł\n`;
  summary += `${st("budgetLabel")}: ${config.defaultBudget} zł/dzień\n\n`;

  if (config.campaignType !== "price-range") {
    summary += `${st("adGroupsHeader")}:\n`;
    for (const g of config.adGroups) {
      summary += `  ▸ ${g.name}\n`;
      summary += `    ${st("cpcLabel")}: ${g.cpc} zł | ${st("budgetLabel")}: ${g.dailyBudget} zł/dzień\n`;
      if (g.offers.length > 0) {
        summary += `    ${st("offersLabel")}: ${g.offers.join(", ")}\n`;
      }
    }
    summary += "\n";
  }

  if (config.campaignType !== "zero-plus-minus" && config.priceRanges.length > 0) {
    summary += `${st("priceRangesHeader")}:\n`;
    for (const pr of config.priceRanges) {
      summary += `  ▸ ${pr.label} (${pr.min}${pr.max ? `–${pr.max}` : "+"} zł)\n`;
    }
    summary += "\n";
  }

  return summary;
}

// ============================================================
// Component
// ============================================================

export default function AllegroAdsBuilder() {
  const { t } = useTranslation("allegroAds");
  const { toast } = useToast();
  const { user } = useAuth();

  // ==================== Client selector (global store) ====================
  const {
    selectedClientId: globalClientId,
    clients: globalAgencyClients,
    isAgencyUser: showClientSelector,
    setSelectedClient: setGlobalSelectedClient,
  } = useAgencyClients();
  const selectedClientId = globalClientId ?? "my-account";
  const myClients =
    (globalAgencyClients as unknown as Array<{
      id: string;
      name: string;
      userId: string | null;
      businessType: string;
      email: string | null;
    }>) || [];
  const selectedClient = myClients.find((c) => c.id === selectedClientId);
  const forUserId = selectedClient?.userId || undefined;

  // ==================== Translated static data ====================
  const allegroPresets = useMemo(() => buildAllegroPresets(t), [t]);
  const placementOptions = useMemo(() => buildPlacementOptions(t), [t]);

  // ==================== State ====================
  const [selectedPresetId, setSelectedPresetId] = useState<string>("dropshipping");
  const [campaignName, setCampaignName] = useState("");
  const [campaignType, setCampaignType] = useState<CampaignType>("zero-plus-minus");
  const [placements, setPlacements] = useState<AdPlacement[]>(["search"]);
  const [defaultCpc, setDefaultCpc] = useState("1.00");
  const [defaultBudget, setDefaultBudget] = useState("10.00");
  const [adGroups, setAdGroups] = useState<AdGroup[]>(() => buildDefaultAdGroups(t));
  const [priceRanges, setPriceRanges] = useState<PriceRange[]>([]);
  const [useSubgroups, setUseSubgroups] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(["g0", "g1", "g-1"]));
  const [newOfferInput, setNewOfferInput] = useState<Record<string, string>>({});
  const [launchResult, setLaunchResult] = useState<LaunchResult | null>(null);
  const [paywallOpen, setPaywallOpen] = useState(false);

  // ==================== Allegro connection status ====================
  const forUserIdParam = forUserId ? `?forUserId=${forUserId}` : "";
  const { data: allegroStatus } = useQuery({
    queryKey: ["allegro-status", forUserId],
    queryFn: () => apiRequest("GET", `/api/integrations/allegro/status${forUserIdParam}`),
    staleTime: 60_000,
  });

  // ==================== Launch mutation ====================
  const launchMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        campaignName:
          campaignName || `Kampania Allegro Ads ${new Date().toLocaleDateString("pl-PL")}`,
        placements,
        adGroups: adGroups.map((g) => ({
          name: g.name,
          cpc: parseFloat(g.cpc) || 1,
          dailyBudget: parseFloat(g.dailyBudget) || 10,
          offerIds: g.offers.filter(Boolean),
        })),
        ...(forUserId ? { forUserId } : {}),
      };
      return await apiRequest("POST", "/api/integrations/allegro/launch-campaign", payload);
    },
    onSuccess: (data: LaunchResult) => {
      setLaunchResult(data);
      toast({
        title: data.success ? t("successToast") : t("partialSuccessToast"),
        description: data.success
          ? t("campaignsCreatedDesc", { count: data.campaigns.length })
          : data.errors.join("; "),
        variant: data.success ? "default" : "destructive",
      });
    },
    onError: (error: Error) => {
      if (error.message.includes("SUBSCRIPTION_REQUIRED") || error.message.includes("403")) {
        setPaywallOpen(true);
        return;
      }
      toast({ title: t("retryTitle"), description: t("retryDesc"), variant: "destructive" });
    },
  });

  // ==================== Preset apply ====================
  const applyPreset = useCallback(
    (presetId: string) => {
      const preset = allegroPresets.find((p) => p.id === presetId);
      if (!preset) return;
      setSelectedPresetId(presetId);
      const c = preset.config;
      if (c.campaignType) setCampaignType(c.campaignType);
      if (c.placements) setPlacements(c.placements);
      if (c.defaultCpc) setDefaultCpc(c.defaultCpc);
      if (c.defaultBudget) setDefaultBudget(c.defaultBudget);
      if (c.adGroups) setAdGroups(c.adGroups);
      if (c.priceRanges) setPriceRanges(c.priceRanges);
      if (c.useSubgroups !== undefined) setUseSubgroups(c.useSubgroups);
      setCampaignName("");
      toast({
        title: t("presetApplied", { label: preset.label }),
        description: preset.description,
      });
    },
    [toast, allegroPresets, t]
  );

  // ==================== Group helpers ====================
  const toggleGroup = (id: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const updateGroup = (id: string, field: keyof AdGroup, value: string) => {
    setAdGroups((prev) => prev.map((g) => (g.id === id ? { ...g, [field]: value } : g)));
  };

  const addOfferToGroup = (groupId: string) => {
    const offerText = (newOfferInput[groupId] || "").trim();
    if (!offerText) return;
    setAdGroups((prev) =>
      prev.map((g) => (g.id === groupId ? { ...g, offers: [...g.offers, offerText] } : g))
    );
    setNewOfferInput((prev) => ({ ...prev, [groupId]: "" }));
  };

  const removeOfferFromGroup = (groupId: string, offerIdx: number) => {
    setAdGroups((prev) =>
      prev.map((g) =>
        g.id === groupId ? { ...g, offers: g.offers.filter((_, i) => i !== offerIdx) } : g
      )
    );
  };

  const addAdGroup = () => {
    const newGroup: AdGroup = {
      id: generateId(),
      name: t("newGroupName", { n: adGroups.length + 1 }),
      tier: "0",
      cpc: defaultCpc,
      dailyBudget: defaultBudget,
      offers: [],
    };
    setAdGroups((prev) => [...prev, newGroup]);
    setExpandedGroups((prev) => new Set([...prev, newGroup.id]));
  };

  const removeAdGroup = (id: string) => {
    setAdGroups((prev) => prev.filter((g) => g.id !== id));
  };

  // ==================== Price range helpers ====================
  const addPriceRange = () => {
    setPriceRanges((prev) => [
      ...prev,
      {
        id: generateId(),
        label: t("newRangeName", { n: prev.length + 1 }),
        min: "",
        max: "",
      },
    ]);
  };

  const updatePriceRange = (id: string, field: keyof PriceRange, value: string) => {
    setPriceRanges((prev) =>
      prev.map((pr) => {
        if (pr.id !== id) return pr;
        const updated = { ...pr, [field]: value };
        if (field === "min" || field === "max") {
          updated.label = `${updated.min || "0"}${updated.max ? `–${updated.max}` : "+"} zł`;
        }
        return updated;
      })
    );
  };

  const removePriceRange = (id: string) => {
    setPriceRanges((prev) => prev.filter((pr) => pr.id !== id));
  };

  // ==================== Placement toggle ====================
  const togglePlacement = (p: AdPlacement) => {
    setPlacements((prev) => (prev.includes(p) ? prev.filter((v) => v !== p) : [...prev, p]));
  };

  // ==================== Copy summary ====================
  const currentConfig: AllegroCampaignConfig = {
    campaignName,
    campaignType,
    placements,
    defaultCpc,
    defaultBudget,
    adGroups,
    priceRanges,
    useSubgroups,
  };

  const canLaunch = (campaignName || adGroups.length > 0) && !launchMutation.isPending;

  // ==================== Total stats ====================
  const totalDailyBudget = useMemo(() => {
    if (campaignType === "price-range") {
      return priceRanges.length * parseFloat(defaultBudget || "0");
    }
    return adGroups.reduce((sum, g) => sum + parseFloat(g.dailyBudget || "0"), 0);
  }, [adGroups, priceRanges, defaultBudget, campaignType]);

  const totalOffers = useMemo(() => {
    return adGroups.reduce((sum, g) => sum + g.offers.length, 0);
  }, [adGroups]);

  // ==================== Render ====================
  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6 overflow-y-auto">
      {/* Header */}
      <div className="flex items-center space-x-3 mb-2">
        <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl flex items-center justify-center">
          <ShoppingBag className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-white">{t("title")}</h2>
          <p className="text-xs text-white/50">{t("subtitle")}</p>
        </div>
      </div>

      {/* Client selector for agency owners / employees */}
      {showClientSelector && myClients.length > 0 && (
        <div className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Users className="w-4 h-4 text-orange-400" />
              <span className="text-sm font-medium text-white">{t("clientSelectorLabel")}</span>
            </div>
            <Select
              value={selectedClientId}
              onValueChange={(val) => {
                if (val === "my-account") {
                  setGlobalSelectedClient(null);
                } else {
                  const client = myClients.find((c) => c.id === val);
                  if (client)
                    setGlobalSelectedClient({
                      id: client.id,
                      userId: client.userId ?? "",
                      name: client.name,
                    });
                }
              }}
            >
              <SelectTrigger className="w-[220px] h-8 text-xs bg-[#030407] border-white/[0.1] text-white">
                <SelectValue placeholder={t("selectClient")} />
              </SelectTrigger>
              <SelectContent className={SELECT_CONTENT_CLS}>
                <SelectItem value="my-account" className={SELECT_ITEM_CLS}>
                  {t("myAccount")}
                </SelectItem>
                {myClients.map((c) => (
                  <SelectItem key={c.id} value={c.id} className={SELECT_ITEM_CLS}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Not connected warning */}
      {allegroStatus && !allegroStatus.connected && (
        <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4 flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-orange-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-white">{t("connectTitle")}</p>
            <p className="text-xs text-white/60 mt-1">{t("connectDesc")}</p>
          </div>
        </div>
      )}

      {/* Launch result */}
      {launchResult && (
        <div
          className={`p-4 rounded-xl border ${launchResult.success ? "bg-green-900/20 border-green-500/30" : "bg-red-900/20 border-red-500/30"}`}
        >
          <div className="flex items-start space-x-3">
            {launchResult.success ? (
              <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-400 mt-0.5" />
            )}
            <div>
              <p className="text-sm font-medium text-white">
                {launchResult.success
                  ? t("resultActive", { count: launchResult.campaigns.length })
                  : t("resultErrors")}
              </p>
              {launchResult.campaigns.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {launchResult.campaigns.map((c, i) => (
                    <li key={i} className="text-xs text-green-400">
                      ✓ {c.campaignName} (ID: {c.campaignId})
                      {c.groups.map((g, gi) => (
                        <span key={gi} className="text-white/40 ml-2">
                          {g.offersAdded > 0 ? `| ${g.offersAdded} ofert` : ""}
                        </span>
                      ))}
                    </li>
                  ))}
                </ul>
              )}
              {launchResult.errors.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {launchResult.errors.map((e, i) => (
                    <li key={i} className="text-xs text-red-400">
                      ✗ {e}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ================ PRESETS / SAVED SETTINGS ================ */}
      <div className={SECTION_CLS}>
        <div className="flex items-center space-x-2 mb-2">
          <Bookmark className="w-4 h-4 text-white/40" />
          <span className="text-xs text-white/50">{t("savedSettings")}</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {allegroPresets.map((preset) => (
            <button
              key={preset.id}
              type="button"
              onClick={() => applyPreset(preset.id)}
              className={`px-4 py-2 rounded-lg text-xs font-medium transition-all border group ${
                selectedPresetId === preset.id
                  ? "bg-orange-500/20 text-white border-orange-500/40 shadow-lg shadow-orange-500/10"
                  : "bg-white/[0.04] text-white/50 border-white/[0.08] hover:text-white/70 hover:border-white/[0.15]"
              }`}
            >
              <div className="flex items-center space-x-2">
                {preset.id === "dropshipping" && <Package className="w-3.5 h-3.5" />}
                {preset.id === "price-segmentation" && <Tag className="w-3.5 h-3.5" />}
                {preset.id === "combined" && <Layers className="w-3.5 h-3.5" />}
                <span>{preset.label}</span>
              </div>
            </button>
          ))}
        </div>
        {selectedPresetId && (
          <p className="text-xs text-white/40 mt-1 pl-6">
            {allegroPresets.find((p) => p.id === selectedPresetId)?.description}
          </p>
        )}
      </div>

      {/* ================ SECTION 1: KAMPANIA ================ */}
      <div className={SECTION_CLS}>
        <h3 className="text-sm font-semibold text-white/80 uppercase tracking-wider flex items-center space-x-2">
          <span className="w-6 h-6 rounded-full bg-orange-500/20 flex items-center justify-center text-xs text-orange-400">
            1
          </span>
          <span>{t("s1Campaign")}</span>
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className={LABEL_CLS}>{t("campaignName")}</Label>
            <Input
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
              placeholder={t("campaignNamePlaceholder")}
              className={INPUT_CLS}
            />
          </div>

          <div>
            <Label className={LABEL_CLS}>{t("campaignType")}</Label>
            <Select
              value={campaignType}
              onValueChange={(v: CampaignType) => {
                setCampaignType(v);
                // Reset sub-state
                if (v === "zero-plus-minus") {
                  setPriceRanges([]);
                  setUseSubgroups(false);
                  if (adGroups.length === 0) {
                    setAdGroups(buildDefaultAdGroups(t));
                  }
                } else if (v === "price-range") {
                  setAdGroups([]);
                  setUseSubgroups(false);
                  if (priceRanges.length === 0) {
                    setPriceRanges([
                      { id: "pr1", label: "100–200 zł", min: "100", max: "200" },
                      { id: "pr2", label: "200–400 zł", min: "200", max: "400" },
                      { id: "pr3", label: "400+ zł", min: "400", max: "" },
                    ]);
                  }
                } else {
                  // combined
                  setUseSubgroups(true);
                  if (priceRanges.length === 0) {
                    setPriceRanges([
                      { id: "pr1", label: "100–200 zł", min: "100", max: "200" },
                      { id: "pr2", label: "200–400 zł", min: "200", max: "400" },
                      { id: "pr3", label: "400+ zł", min: "400", max: "" },
                    ]);
                  }
                  if (adGroups.length === 0) {
                    setAdGroups(buildDefaultAdGroups(t));
                  }
                }
              }}
            >
              <SelectTrigger className={SELECT_TRIGGER_CLS}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className={SELECT_CONTENT_CLS}>
                <SelectItem value="zero-plus-minus" className={SELECT_ITEM_CLS}>
                  {t("typeZeroPlusMinus")}
                </SelectItem>
                <SelectItem value="price-range" className={SELECT_ITEM_CLS}>
                  {t("typePriceRange")}
                </SelectItem>
                <SelectItem value="combined" className={SELECT_ITEM_CLS}>
                  {t("typeCombined")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className={LABEL_CLS}>{t("defaultCpcLabel")}</Label>
            <Input
              type="number"
              step="0.01"
              min="0.01"
              value={defaultCpc}
              onChange={(e) => setDefaultCpc(e.target.value)}
              placeholder="1.00"
              className={INPUT_CLS}
            />
          </div>
          <div>
            <Label className={LABEL_CLS}>{t("defaultBudgetLabel")}</Label>
            <Input
              type="number"
              step="0.50"
              min="1"
              value={defaultBudget}
              onChange={(e) => setDefaultBudget(e.target.value)}
              placeholder="10.00"
              className={INPUT_CLS}
            />
          </div>
        </div>
      </div>

      {/* ================ SECTION 2: MIEJSCA EMISJI ================ */}
      <div className={SECTION_CLS}>
        <h3 className="text-sm font-semibold text-white/80 uppercase tracking-wider flex items-center space-x-2">
          <span className="w-6 h-6 rounded-full bg-orange-500/20 flex items-center justify-center text-xs text-orange-400">
            2
          </span>
          <span>{t("s2Placements")}</span>
        </h3>

        <div className="space-y-2">
          {placementOptions.map((opt) => (
            <label
              key={opt.value}
              className={`flex items-center space-x-3 px-4 py-3 rounded-lg border transition-all cursor-pointer ${
                placements.includes(opt.value)
                  ? opt.recommended
                    ? "bg-green-500/10 border-green-500/30"
                    : "bg-orange-500/10 border-orange-500/30"
                  : "bg-white/[0.02] border-white/[0.06] hover:border-white/[0.12]"
              }`}
            >
              <Checkbox
                checked={placements.includes(opt.value)}
                onCheckedChange={() => togglePlacement(opt.value)}
              />
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <span
                    className={`text-sm font-medium ${placements.includes(opt.value) ? "text-white" : "text-white/60"}`}
                  >
                    {opt.label}
                  </span>
                  {opt.recommended && (
                    <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase bg-green-500/20 text-green-400 rounded">
                      {t("recommended")}
                    </span>
                  )}
                </div>
                <p className="text-xs text-white/40 mt-0.5">{opt.description}</p>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* ================ SECTION 3: SEGMENTY CENOWE (price-range / combined) ================ */}
      {(campaignType === "price-range" || campaignType === "combined") && (
        <div className={SECTION_CLS}>
          <h3 className="text-sm font-semibold text-white/80 uppercase tracking-wider flex items-center space-x-2">
            <span className="w-6 h-6 rounded-full bg-orange-500/20 flex items-center justify-center text-xs text-orange-400">
              3
            </span>
            <span>{t("s3PriceSegments")}</span>
          </h3>
          <p className="text-xs text-white/40">{t("segmentsDesc")}</p>

          <div className="space-y-3">
            {priceRanges.map((pr, idx) => (
              <div
                key={pr.id}
                className="flex items-center space-x-3 bg-white/[0.03] border border-white/[0.08] rounded-lg px-4 py-3"
              >
                <Tag className="w-4 h-4 text-amber-400 flex-shrink-0" />
                <div className="flex-1 grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-[10px] text-white/40">{t("segmentLabelField")}</Label>
                    <Input
                      value={pr.label}
                      onChange={(e) => updatePriceRange(pr.id, "label", e.target.value)}
                      className="h-7 text-xs bg-[#030407] border-white/[0.1] text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] text-white/40">{t("segmentFrom")}</Label>
                    <Input
                      type="number"
                      value={pr.min}
                      onChange={(e) => updatePriceRange(pr.id, "min", e.target.value)}
                      placeholder="0"
                      className="h-7 text-xs bg-[#030407] border-white/[0.1] text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] text-white/40">{t("segmentTo")}</Label>
                    <Input
                      type="number"
                      value={pr.max}
                      onChange={(e) => updatePriceRange(pr.id, "max", e.target.value)}
                      placeholder="∞"
                      className="h-7 text-xs bg-[#030407] border-white/[0.1] text-white"
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removePriceRange(pr.id)}
                  className="text-white/30 hover:text-red-400 transition-colors p-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={addPriceRange}
            className="flex items-center space-x-2 px-4 py-2 text-xs text-white/50 hover:text-white/70 border border-dashed border-white/[0.1] hover:border-white/[0.2] rounded-lg transition-all"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>{t("addSegment")}</span>
          </button>

          {campaignType === "combined" && (
            <div className="flex items-center space-x-3 pt-2 border-t border-white/[0.06]">
              <Checkbox checked={useSubgroups} onCheckedChange={(v) => setUseSubgroups(!!v)} />
              <div>
                <span className="text-sm text-white/70">{t("subgroupsLabel")}</span>
                <p className="text-xs text-white/40">{t("subgroupsDesc")}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ================ SECTION 4: GRUPY REKLAM (zero-plus-minus / combined) ================ */}
      {(campaignType === "zero-plus-minus" || (campaignType === "combined" && useSubgroups)) && (
        <div className={SECTION_CLS}>
          <h3 className="text-sm font-semibold text-white/80 uppercase tracking-wider flex items-center space-x-2">
            <span className="w-6 h-6 rounded-full bg-orange-500/20 flex items-center justify-center text-xs text-orange-400">
              {campaignType === "combined" ? "4" : "3"}
            </span>
            <span>{t("s4AdGroups")}</span>
          </h3>

          {campaignType === "zero-plus-minus" && (
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
              <p className="text-xs text-blue-300">
                <strong>{t("systemInfoStrong")}</strong> {t("systemInfo")}
              </p>
            </div>
          )}

          <div className="space-y-3">
            {adGroups.map((group) => {
              const colors = TIER_COLORS[group.tier] || TIER_COLORS["0"];
              const isExpanded = expandedGroups.has(group.id);

              return (
                <div
                  key={group.id}
                  className={`${colors.bg} border ${colors.border} rounded-xl overflow-hidden transition-all`}
                >
                  {/* Group header */}
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.id)}
                    className="w-full flex items-center justify-between px-4 py-3 text-left"
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-3 h-3 rounded-full ${colors.dot}`} />
                      <span className={`text-sm font-semibold ${colors.text}`}>{group.name}</span>
                      <span className="text-xs text-white/40">
                        {t("groupStats", { cpc: group.cpc, budget: group.dailyBudget })}
                        {group.offers.length > 0 &&
                          ` ${t("groupOfferCount", { count: group.offers.length })}`}
                      </span>
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="w-4 h-4 text-white/40" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-white/40" />
                    )}
                  </button>

                  {/* Group body */}
                  {isExpanded && (
                    <div className="px-4 pb-4 space-y-3 border-t border-white/[0.06]">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-3">
                        <div>
                          <Label className="text-[10px] text-white/40">{t("groupNameLabel")}</Label>
                          <Input
                            value={group.name}
                            onChange={(e) => updateGroup(group.id, "name", e.target.value)}
                            className="h-8 text-xs bg-[#030407]/50 border-white/[0.1] text-white"
                          />
                        </div>
                        <div>
                          <Label className="text-[10px] text-white/40">{t("groupCpcLabel")}</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0.01"
                            value={group.cpc}
                            onChange={(e) => updateGroup(group.id, "cpc", e.target.value)}
                            className="h-8 text-xs bg-[#030407]/50 border-white/[0.1] text-white"
                          />
                        </div>
                        <div>
                          <Label className="text-[10px] text-white/40">
                            {t("groupBudgetLabel")}
                          </Label>
                          <Input
                            type="number"
                            step="0.50"
                            min="1"
                            value={group.dailyBudget}
                            onChange={(e) => updateGroup(group.id, "dailyBudget", e.target.value)}
                            className="h-8 text-xs bg-[#030407]/50 border-white/[0.1] text-white"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-[10px] text-white/40">{t("groupTierLabel")}</Label>
                        <Select
                          value={group.tier}
                          onValueChange={(v: "0" | "+1" | "-1") => updateGroup(group.id, "tier", v)}
                        >
                          <SelectTrigger className="h-8 text-xs bg-[#030407]/50 border-white/[0.1] text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className={SELECT_CONTENT_CLS}>
                            <SelectItem value="0" className={SELECT_ITEM_CLS}>
                              {t("tier0")}
                            </SelectItem>
                            <SelectItem value="+1" className={SELECT_ITEM_CLS}>
                              {t("tierPlus1")}
                            </SelectItem>
                            <SelectItem value="-1" className={SELECT_ITEM_CLS}>
                              {t("tierMinus1")}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Offers list */}
                      <div>
                        <Label className="text-[10px] text-white/40">{t("offersInGroup")}</Label>
                        {group.offers.length > 0 && (
                          <div className="space-y-1 mt-1 mb-2">
                            {group.offers.map((offer, idx) => (
                              <div
                                key={idx}
                                className="flex items-center space-x-2 bg-white/[0.03] rounded px-2 py-1"
                              >
                                <span className="text-xs text-white/60 flex-1 truncate">
                                  {offer}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => removeOfferFromGroup(group.id, idx)}
                                  className="text-white/30 hover:text-red-400"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="flex space-x-2">
                          <Input
                            value={newOfferInput[group.id] || ""}
                            onChange={(e) =>
                              setNewOfferInput((prev) => ({ ...prev, [group.id]: e.target.value }))
                            }
                            onKeyDown={(e) => e.key === "Enter" && addOfferToGroup(group.id)}
                            placeholder={t("offerPlaceholder")}
                            className="h-7 text-xs bg-[#030407]/50 border-white/[0.1] text-white placeholder:text-white/30 flex-1"
                          />
                          <button
                            type="button"
                            onClick={() => addOfferToGroup(group.id)}
                            className="px-2 py-1 text-xs text-white/50 hover:text-white bg-white/[0.06] rounded border border-white/[0.1] hover:border-white/[0.2] transition-colors"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                      </div>

                      {adGroups.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeAdGroup(group.id)}
                          className="text-xs text-red-400/60 hover:text-red-400 transition-colors"
                        >
                          {t("removeGroup")}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <button
            type="button"
            onClick={addAdGroup}
            className="flex items-center space-x-2 px-4 py-2 text-xs text-white/50 hover:text-white/70 border border-dashed border-white/[0.1] hover:border-white/[0.2] rounded-lg transition-all"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>{t("addGroup")}</span>
          </button>
        </div>
      )}

      {/* ================ SUMMARY ================ */}
      <div className={SECTION_CLS}>
        <h3 className="text-sm font-semibold text-white/80 uppercase tracking-wider flex items-center space-x-2">
          <span className="w-6 h-6 rounded-full bg-orange-500/20 flex items-center justify-center text-xs text-orange-400">
            <Eye className="w-3 h-3" />
          </span>
          <span>{t("summaryTitle")}</span>
        </h3>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white/[0.03] rounded-lg p-3 text-center">
            <p className="text-lg font-bold text-orange-400">{defaultCpc} zł</p>
            <p className="text-[10px] text-white/40 uppercase">{t("statCpcLabel")}</p>
          </div>
          <div className="bg-white/[0.03] rounded-lg p-3 text-center">
            <p className="text-lg font-bold text-orange-400">{totalDailyBudget.toFixed(2)} zł</p>
            <p className="text-[10px] text-white/40 uppercase">{t("statBudgetLabel")}</p>
          </div>
          <div className="bg-white/[0.03] rounded-lg p-3 text-center">
            <p className="text-lg font-bold text-orange-400">
              {campaignType === "price-range" ? priceRanges.length : adGroups.length}
            </p>
            <p className="text-[10px] text-white/40 uppercase">
              {campaignType === "price-range" ? t("statSegmentsLabel") : t("statGroupsLabel")}
            </p>
          </div>
          <div className="bg-white/[0.03] rounded-lg p-3 text-center">
            <p className="text-lg font-bold text-orange-400">{placements.length}</p>
            <p className="text-[10px] text-white/40 uppercase">{t("statPlacementsLabel")}</p>
          </div>
        </div>

        {/* Preview */}
        <div className="bg-[#030407] border border-white/[0.08] rounded-lg p-4 font-mono text-xs text-white/60 whitespace-pre-wrap max-h-60 overflow-y-auto">
          {formatCampaignSummary(currentConfig, t, placementOptions)}
        </div>
      </div>

      {/* ================ ACTIONS ================ */}
      <div className="flex items-center justify-between pb-8">
        <p className="text-xs text-white/40">
          {t("launchNote")} <span className="text-green-400">{t("launchNoteStatus")}</span>{" "}
          {t("launchNoteSuffix")}
        </p>
        <Button
          onClick={() => launchMutation.mutate()}
          disabled={!canLaunch || !allegroStatus?.connected}
          className="bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white px-8 py-2.5"
        >
          {launchMutation.isPending ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {t("launching")}
            </>
          ) : (
            <>
              <Rocket className="w-4 h-4 mr-2" />
              {t("launchBtn")}
            </>
          )}
        </Button>
      </div>

      <PaywallModal open={paywallOpen} onClose={() => setPaywallOpen(false)} />
    </div>
  );
}
